import { AMEND, CANCEL } from '@/src/constants/itinerary/fareFamily'
import { FareFamilyFareRule, ProcessedFareRule } from '@/src/types/fareFamilyInfo'
import { Fare } from '@/src/types/itinerary'
import { isEmpty } from '@/src/utils/generalUtils/isEmpty'
import { processFareRuleCharges } from '@/src/utils/itinerary/fareFamily'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'

interface UseFareRuleProcessorProps {
    fareFamilyInfoData: any
    tabValue: 0 | 1
    selectedFare: Fare | undefined
    formatDate: (date: any) => string
    formatCurrency: (amount: any) => string
}

const useFareRuleProcessor = ({
    fareFamilyInfoData,
    tabValue,
    selectedFare,
    formatDate,
    formatCurrency
}: UseFareRuleProcessorProps) => {
    const transparencyWidgetTranslations = useTranslations('itineraryPage.transparencyWidget')
    const commonTranslations = useTranslations('common')
    const tabValueIndexed:'1' | '2' = String(tabValue + 1) as '1' | '2'

    const { cancellationRefundPolicy, amendRefundPolicy } = useMemo(() => {
        const ctBrandName = selectedFare?.fareGroupInformationMap?.[tabValueIndexed]?.ctBrandName || ''
        const solutions = fareFamilyInfoData?.data?.solutions || []
        const fareRulesData = solutions[tabValue]?.fareFamilyFareRules?.[ctBrandName] || {}
        const fareRules = fareRulesData.fareRules || []

        if (!Array.isArray(fareRules) || isEmpty(fareRules)) {
            return {
                cancellationRefundPolicy: {} as FareFamilyFareRule,
                amendRefundPolicy: {} as FareFamilyFareRule
            }
        }

        const cancellationRefundPolicy = fareRules.find(rule => rule.type === CANCEL) || ({} as FareFamilyFareRule)
        const amendRefundPolicy = fareRules.find(rule => rule.type === AMEND) || ({} as FareFamilyFareRule)

        return { cancellationRefundPolicy, amendRefundPolicy }
    }, [fareFamilyInfoData, tabValue, selectedFare])

    const processedFareRules = useMemo(() => {
        const fareRules = [cancellationRefundPolicy, amendRefundPolicy].filter(rule => !isEmpty(rule))

        return fareRules.map((fareRule): ProcessedFareRule => {
            const { type, charges = [], permitted } = fareRule
            const segmentCount = charges.length
            const { timeSegments, priceSegments } = processFareRuleCharges(
                fareRule,
                formatDate,
                formatCurrency,
                commonTranslations
            )

            const title =
                type === CANCEL
                    ? transparencyWidgetTranslations('cancellationRefundPolicyText')
                    : transparencyWidgetTranslations('dateChangeRefundPolicyText')

            return {
                type,
                charges,
                permitted,
                timeSegments,
                priceSegments,
                segmentCount,
                title
            }
        })
    }, [
        cancellationRefundPolicy,
        amendRefundPolicy,
        formatDate,
        formatCurrency,
        transparencyWidgetTranslations,
        commonTranslations
    ])

    return {
        cancellationRefundPolicy,
        amendRefundPolicy,
        processedFareRules
    }
}

export default useFareRuleProcessor
